import React from 'react';

type FillHeightInput = {
    /** `window.innerHeight`. */
    viewportHeight: number;
    /** The element's top edge in document coordinates (not the viewport's). */
    top: number;
    /** Space to leave between the element's bottom edge and the viewport's. */
    bottomGapPx: number;
    /** The element never gets shorter than this; the page scrolls instead. */
    minHeightPx: number;
};

/**
 * The height that takes an element from its top edge down to `bottomGapPx`
 * above the bottom of the viewport, never below `minHeightPx`.
 *
 * Rounded down: a fractional top rounded up would leave the page a pixel too
 * tall, and a page one pixel too tall grows a scrollbar.
 */
export const getFillHeight = ({
    viewportHeight,
    top,
    bottomGapPx,
    minHeightPx,
}: FillHeightInput): number =>
    Math.max(minHeightPx, Math.floor(viewportHeight - top - bottomGapPx));

type FillViewportHeightOptions = Pick<
    FillHeightInput,
    'bottomGapPx' | 'minHeightPx'
>;

/**
 * Sizes an element to fill the viewport from wherever it starts down to
 * `bottomGapPx` above the bottom edge (BOS-193).
 *
 * The top is read in document coordinates (`rect.top + scrollY`), so a page
 * that has scrolled because the element sits at its minimum measures the same
 * as one that has not.
 *
 * Two things move the element without it knowing: the window resizing, and
 * the content above it changing height (a filter row wrapping onto a second
 * line, a chip row appearing). The first is the `resize` event. The second
 * changes the height of whichever ancestor holds that content, so every
 * ancestor below `<body>` is observed. Watching only the page root would not
 * do: the app shell root is `min-h-screen`, so content above that *shrinks*
 * never resizes it, and the element would keep a gap at the bottom.
 *
 * `height` is `undefined` until the first measurement, which happens in a
 * layout effect, before the browser paints.
 */
export const useFillViewportHeight = <T extends HTMLElement>({
    bottomGapPx,
    minHeightPx,
}: FillViewportHeightOptions) => {
    const ref = React.useRef<T>(null);
    const [height, setHeight] = React.useState<number | undefined>(undefined);

    React.useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return;
        }

        const measure = () => {
            setHeight(
                getFillHeight({
                    viewportHeight: window.innerHeight,
                    top: element.getBoundingClientRect().top + window.scrollY,
                    bottomGapPx,
                    minHeightPx,
                }),
            );
        };

        measure();
        window.addEventListener('resize', measure);

        const observer =
            typeof ResizeObserver === 'function'
                ? new ResizeObserver(measure)
                : null;
        for (
            let node = element.parentElement;
            node && node !== document.body;
            node = node.parentElement
        ) {
            observer?.observe(node);
        }

        return () => {
            window.removeEventListener('resize', measure);
            observer?.disconnect();
        };
    }, [bottomGapPx, minHeightPx]);

    return { ref, height };
};
